// Entry guard for the submission flow.
//
// Every flow URL must carry a breakdown ref (`bd`); `roleid` is optional. With
// no breakdown there is nothing to submit to, so those visitors go to the
// traditional actor signup instead of a wizard with no target.

// @ts-ignore
import qs from "query-string";
import { hasPersistedFlow } from "./persistedFlow";
import { parseSubmissionParams } from "./submissionParams";

// The traditional signup wizard.
export const SIGNUP_FALLBACK_PATH = "/register/actor";

// Where the traditional signup should land the new account once it finishes.
// Read outside this app, so the value is a contract: don't rename it here
// without the consumer.
export const LANDING_PARAM = "page";
export const LANDING_BREAKDOWNS = "breakdowns";

// `extraParams` merges into whatever the current URL already carries (bd,
// roleid, campaign tags, ?ctry) rather than being appended blindly — the
// search string is almost never empty at the point this is called.
export const fallbackTo = (
  search?: string,
  extraParams?: { [key: string]: string }
): string => {
  if (!extraParams) return `${SIGNUP_FALLBACK_PATH}${search || ""}`;

  let params: any = {};
  try {
    params = qs.parse(search || "") || {};
  } catch (e) {
    params = {};
  }
  Object.keys(extraParams).forEach((key) => {
    params[key] = extraParams[key];
  });
  const out = qs.stringify(params);
  return `${SIGNUP_FALLBACK_PATH}${out ? `?${out}` : ""}`;
};

// Whether the closed/expired gate should PUSH or REPLACE.
//
// It depends entirely on what Back would land on:
//
//   castingCalls — the browse list. A stable page that renders a list and
//                  redirects nobody, so pushing is right: Back returns the
//                  visitor to the list they were choosing from, which is the
//                  only sensible place to be after "that one is closed".
//   boost        — the ad landing page gates on load. Pushing would make Back
//                  bounce: boost → gate → boost → gate. Replace, so Back skips
//                  past it to wherever they came from.
//   direct       — a hand-typed or shared step URL. There is no previous page
//                  inside the flow to return to, so replacing loses nothing.
//
// Mid-flow gates (a deadline that passes while someone is on a step) always
// replace, whatever the entry source: Back would land on the step, and the step
// re-runs the gate on mount.
export const gateNavigation = (
  entrySource?: string | null
): "push" | "replace" => (entrySource === "castingCalls" ? "push" : "replace");

interface FlowContextInput {
  // location.search of the current route
  search?: string;
  // breakdownId from the Rematch store, when the caller already has it: covers
  // in-session navigation on a device where sessionStorage writes are blocked.
  breakdownId?: string | null;
  // Set on the verification screen: a user who was already emailed a code must
  // be able to finish even if the flow record is gone, or the code is wasted.
  hasSignupSession?: boolean;
}

export const hasFlowContext = ({
  search,
  breakdownId,
  hasSignupSession,
}: FlowContextInput): boolean => {
  if (breakdownId) return true;
  if (hasSignupSession) return true;
  // `bd` on the URL is a valid cold entry — the page fetches it and starts
  // the flow from there.
  if (parseSubmissionParams(search).bd) return true;
  // A flow already started in this tab keeps its own breakdown ref.
  return hasPersistedFlow();
};
